import { useCallback, useEffect, useState } from 'react';
import { fetchAgents, type AgentRow } from '../api/agents';
import {
  fetchInsights,
  fetchScheduleExecutions,
  fetchUsage,
  sumUsage,
  type InstanceUsage,
  type Insight,
  type ScheduleExecution,
} from '../api/overview';
import './OverviewPage.css';

/**
 * What this instance is, right now — agents, cost, and what is scheduled.
 *
 * Ported from the predecessor's `Dashboard`. It reads only endpoints the
 * framework itself serves, so it works on any instance regardless of which
 * extensions are enabled — which is why it, rather than the knowledge
 * Dashboard, is what `/` falls back to.
 *
 * **Each panel fails on its own.** The predecessor used one `Promise.all`
 * with a `.catch` on the schedule call alone, so a failure of agents,
 * usage or insights emptied the whole page. Here a panel that could not
 * load says so and the others still render.
 *
 * **Token totals skip instances whose store could not be read.** Those
 * rows carry `error` instead of counters, and the predecessor's
 * `usage.reduce((s, u) => s + u.total_input_tokens, 0)` turned one into
 * `NaN` for the whole total — which renders as a dash and reads as "no
 * usage yet". `sumUsage` skips them and the count of skipped rows is
 * shown, because a total over fewer instances than exist is a different
 * number and the reader should know.
 */

const REFRESH_MS = 15_000;

function compact(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function uptime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const min = Math.floor(seconds / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ${min % 60}m`;
  return `${Math.floor(hr / 24)}d ${hr % 24}h`;
}

function relative(ms: number | null | undefined): string {
  if (!ms) return 'never';
  const min = Math.floor((Date.now() - ms) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/** A panel's own state: not asked yet, failed, or an answer. */
type Panel<T> = { data: T } | { error: string } | null;

function panelError<T>(p: Panel<T>): string | null {
  return p !== null && 'error' in p ? p.error : null;
}

function panelData<T>(p: Panel<T>): T | null {
  return p !== null && 'data' in p ? p.data : null;
}

/**
 * Resolve one promise into one panel — an answer or the reason there is
 * none, never an unhandled rejection.
 *
 * A panel that failed has to be distinguishable from a panel still
 * loading (`null`) and from a panel that legitimately answered nothing
 * (`{data: []}`). Collapsing any two of those is how "no agents
 * configured" and "the agents endpoint is down" come to look the same.
 */
function fill<T>(
  promise: Promise<T>,
  set: (panel: Panel<T>) => void,
): void {
  promise.then(
    (data) => set({ data }),
    (reason: unknown) => set({ error: String(reason) }),
  );
}

export function OverviewPage() {
  const [agents, setAgents] = useState<Panel<AgentRow[]>>(null);
  const [usage, setUsage] = useState<Panel<InstanceUsage[]>>(null);
  const [insights, setInsights] = useState<Panel<Insight[]>>(null);
  const [schedule, setSchedule] = useState<Panel<ScheduleExecution[]>>(null);

  const load = useCallback(() => {
    // Four independent reads, each settling into its own panel. Not one
    // `Promise.all`, and not one `allSettled` over all four either: a
    // panel that resolves first should render first, rather than waiting
    // on the slowest of the four to decide whether any of them can show.
    fill(fetchAgents(), setAgents);
    fill(fetchUsage(), setUsage);
    fill(fetchInsights(), setInsights);
    fill(fetchScheduleExecutions(10), setSchedule);
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const agentRows = panelData(agents) ?? [];
  const usageRows = panelData(usage) ?? [];
  const insightRows = panelData(insights) ?? [];
  const scheduleRows = panelData(schedule) ?? [];

  const running = agentRows.filter((a) => a.status === 'running').length;
  const unreadable = usageRows.filter((u) => u.error).length;
  const tokens =
    sumUsage(usageRows, 'total_input_tokens') +
    sumUsage(usageRows, 'total_output_tokens');

  return (
    <div className="ov-page">
      <header className="ov-page__header">
        <h1 className="ov-page__title">Overview</h1>
        <p className="ov-page__note">
          What this instance is running, and what it has cost.
        </p>
      </header>

      <div className="ov-stats">
        <Stat
          label="agents"
          value={
            agents === null
              ? '…'
              : `${running}/${agentRows.length}`
          }
          hint="running of configured"
          error={panelError(agents)}
        />
        <Stat
          label="tokens"
          value={usage === null ? '…' : compact(tokens)}
          hint={
            unreadable > 0
              ? `in + out · ${unreadable} instance${unreadable === 1 ? '' : 's'} unreadable and excluded`
              : 'in + out, all instances'
          }
          error={panelError(usage)}
          warn={unreadable > 0}
        />
        <Stat
          label="sessions"
          value={
            usage === null ? '…' : String(sumUsage(usageRows, 'total_sessions'))
          }
          hint="recorded"
          error={panelError(usage)}
        />
        <Stat
          label="turns"
          value={
            usage === null ? '…' : String(sumUsage(usageRows, 'total_turns'))
          }
          hint="recorded"
          error={panelError(usage)}
        />
      </div>

      <section className="ov-section">
        <h2 className="ov-section__title">Agents</h2>
        {panelError(agents) ? (
          <p className="ov-error">{panelError(agents)}</p>
        ) : agents === null ? (
          <p className="ov-dim">Loading…</p>
        ) : agentRows.length === 0 ? (
          <p className="ov-dim">
            No agents are configured for this instance.
          </p>
        ) : (
          <ul className="ov-agents">
            {agentRows.map((a) => (
              <li
                key={a.instance_id}
                className={`ov-agent ov-agent--${a.status}`}
              >
                <div className="ov-agent__top">
                  <span className="ov-agent__name">{a.class_id}</span>
                  <span className={`ov-badge ov-badge--${a.status}`}>
                    {a.status}
                  </span>
                </div>
                <div className="ov-agent__row ov-dim">
                  <span>{a.model ?? 'no model'}</span>
                  <span>·</span>
                  <span>up {uptime(a.uptime_s)}</span>
                </div>
                <div className="ov-agent__row ov-dim">
                  <span>
                    {a.channels.length > 0
                      ? a.channels.join(', ')
                      : 'no channel'}
                  </span>
                  <span>·</span>
                  <span>
                    {a.session_count} session
                    {a.session_count === 1 ? '' : 's'} ·{' '}
                    {relative(a.last_active_ms)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="ov-columns">
        <section className="ov-section">
          <h2 className="ov-section__title">Insights</h2>
          {panelError(insights) ? (
            <p className="ov-error">{panelError(insights)}</p>
          ) : insights === null ? (
            <p className="ov-dim">Loading…</p>
          ) : insightRows.length === 0 ? (
            <p className="ov-dim">
              Nothing is scheduled. A scheduled task's latest result appears
              here.
            </p>
          ) : (
            <ul className="ov-list">
              {insightRows.map((i) => (
                <li
                  key={i.type}
                  className={`ov-insight${i.available ? ' is-available' : ''}`}
                >
                  <span className="ov-insight__label">{i.label}</span>
                  <span className="ov-dim">{i.schedule}</span>
                  <span
                    className={`ov-badge ov-badge--${i.available ? 'running' : 'idle'}`}
                    title={
                      i.available
                        ? 'Has produced output that can be read'
                        : 'Scheduled, but has produced nothing yet'
                    }
                  >
                    {i.available ? 'ready' : 'empty'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="ov-section">
          <h2 className="ov-section__title">Recent schedule</h2>
          {panelError(schedule) ? (
            <p className="ov-error">{panelError(schedule)}</p>
          ) : schedule === null ? (
            <p className="ov-dim">Loading…</p>
          ) : scheduleRows.length === 0 ? (
            <p className="ov-dim">
              Nothing scheduled has executed. Procedures on a timer are
              listed under <strong>Procedures</strong>, which distinguishes
              a trigger that has never fired from one that is merely idle.
            </p>
          ) : (
            <ul className="ov-list">
              {scheduleRows.map((e, i) => (
                <li key={e.id ?? i} className="ov-sched">
                  <span className="ov-sched__name">
                    {e.procedure_name ?? '(unnamed)'}
                  </span>
                  <span className="ov-dim">
                    {relative(e.finished_at_ms ?? e.started_at_ms)}
                  </span>
                  {e.status && (
                    <span className={`ov-badge ov-badge--${e.status}`}>
                      {e.status}
                    </span>
                  )}
                  {e.error && <span className="ov-sched__err">{e.error}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  error,
  warn,
}: {
  label: string;
  value: string;
  hint: string;
  error: string | null;
  warn?: boolean;
}) {
  return (
    <div className={`ov-stat${error ? ' is-error' : ''}`}>
      <span className="ov-stat__label">{label}</span>
      <span className="ov-stat__value">{error ? '—' : value}</span>
      <span className={`ov-stat__hint${warn ? ' is-warn' : ''}`}>
        {error ? 'could not be read' : hint}
      </span>
    </div>
  );
}
